import { lazy } from "react";
import type { ComponentType } from "react";

const LAST_RELOAD_KEY = "lazy-chunk-reload-at";

/**
 * One reload per minute: enough to heal post-deploy skew (the reloaded page
 * gets fresh chunk URLs), while a chunk that keeps failing for another reason
 * (offline, extension blocking) falls through to the error boundary instead
 * of reload-looping.
 */
const shouldReload = (): boolean => {
  try {
    const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0);
    if (Date.now() - lastReload < 60_000) return false;
    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

/**
 * React.lazy that self-heals post-deploy version skew. A browser still running
 * a pre-deploy bundle requests lazy chunks by their old content-hashed URLs,
 * which no longer exist on the new deployment — the dynamic import rejects
 * (ChunkLoadError) and the route crashes into the error boundary. One full
 * reload fetches the fresh index.html, whose chunk URLs resolve again.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own ComponentType<any> constraint
export const lazyWithReload = <T extends ComponentType<any>>(
  importComponent: () => Promise<{ default: T }>,
) =>
  lazy(() =>
    importComponent().catch((error: unknown) => {
      if (shouldReload()) {
        window.location.reload();
        // Hold Suspense on its fallback while the reload tears the page down,
        // but never forever: React.lazy memoises this promise, so if the
        // reload doesn't take effect (seen in jetsam-restored WebViews) an
        // unsettled promise would suspend the route for the page's lifetime.
        // Rejecting surfaces the original failure to the error boundary.
        return new Promise<{ default: T }>((_, reject) => {
          window.setTimeout(() => reject(error), 3_000);
        });
      }
      throw error;
    }),
  );
